import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { AdminLayout } from "../../components/admin/AdminLayout";
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
} from "../../hooks/useCategories";
import { CategoryForm } from "../../components/admin/CategoryForm";
import { DeleteConfirmationDialog } from "../../components/admin/DeleteConfirmationDialog";
import { CategoryListItem } from "../../components/admin/CategoryListItem";
import type { CategoryResponse, CategoryCreate, CategoryUpdate } from "../../api/generated/types.gen";

// Alias for convenience
type Category = CategoryResponse;

export const CategoryManagementPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);

  const { data: categoriesData, isLoading } = useCategories();
  const createCategoryMutation = useCreateCategory();
  const updateCategoryMutation = useUpdateCategory();
  const deleteCategoryMutation = useDeleteCategory();

  const categories = categoriesData?.items || [];

  const filteredCategories = categories.filter(
    (category) =>
      category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (category.description || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
  );

  const handleCreate = () => {
    setEditingCategory(null);
    setFormOpen(true);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormOpen(true);
  };

  const handleDelete = (category: Category) => {
    setDeletingCategory(category);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deletingCategory) {
      try {
        await deleteCategoryMutation.mutateAsync(deletingCategory.id);
        setDeleteDialogOpen(false);
        setDeletingCategory(null);
      } catch (error) {
        // Error is handled by the mutation
      }
    }
  };

  const handleFormSubmit = async (data: CategoryCreate | CategoryUpdate) => {
    try {
      if (editingCategory) {
        await updateCategoryMutation.mutateAsync({
          categoryId: editingCategory.id,
          data: data as CategoryUpdate,
        });
      } else {
        await createCategoryMutation.mutateAsync(data as CategoryCreate);
      }
      setFormOpen(false);
      setEditingCategory(null);
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <Typography>Loading categories...</Typography>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            mb: { xs: 3, sm: 4 },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                mb: 1,
                fontSize: { xs: "1.5rem", sm: "2rem" },
              }}
            >
              Category Management
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              Manage your photography categories
            </Typography>
          </Box>
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{
              textTransform: "none",
              fontSize: { xs: "0.875rem", sm: "1rem" },
              px: { xs: 2, sm: 2 },
              py: { xs: 1.25, sm: 1 },
              minHeight: { xs: "44px", sm: "40px" },
              width: { xs: "100%", sm: "auto" },
              fontWeight: 500,
              border: "none",
              boxShadow: "none",
            }}
          >
            New Category
          </Button>
        </Box>

        {/* Search */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <TextField
            fullWidth
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: "#fff",
              "& .MuiInputBase-root": {
                fontSize: { xs: "16px", sm: "16px" }, // Prevent zoom on iOS
                minHeight: { xs: "48px", sm: "40px" }, // Better touch target on mobile
              },
              "& .MuiInputBase-input": {
                padding: { xs: "12px 14px", sm: "10px 14px" },
              },
              "& .MuiInput-underline:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:hover:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:after": {
                borderBottom: "none",
              },
            }}
          />
        </Box>

        {/* Category List */}
        {filteredCategories.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              No categories found
            </Typography>
          </Box>
        ) : (
          <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
            {filteredCategories.map((category) => (
              <Box component="li" key={category.id}>
                <CategoryListItem
                  category={category}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* Summary */}
        <Box sx={{ mt: { xs: 2, sm: 3 } }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: { xs: "0.875rem", sm: "0.875rem" },
            }}
          >
            Showing {filteredCategories.length} of {categories.length} categories
          </Typography>
        </Box>
      </Box>

      {/* Category Form Modal */}
      <CategoryForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingCategory(null);
        }}
        onSubmit={handleFormSubmit}
        category={editingCategory}
        isLoading={
          createCategoryMutation.isPending || updateCategoryMutation.isPending
        }
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeletingCategory(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Category"
        message={
          deletingCategory
            ? `Are you sure you want to delete "${deletingCategory.name}"? This action cannot be undone.`
            : ""
        }
        isLoading={deleteCategoryMutation.isPending}
      />
    </AdminLayout>
  );
};
